import React, { useCallback, useEffect, useRef, useState } from "react";
import { Select } from "../../../../../components/Select";
import { Button } from "../../../../../components/Button";
import { TagValueInput, SwatchVariacao, chaveSwatch } from "../../../../../components/TagValueInput/TagValueInput.tsx";
import { ModalEditarVariacao } from "../../../../../components/ModalEditarVariacao/ModalEditarVariacao.tsx";
import { useTheme } from "../../../../../contexts/Theme/ThemeContext.tsx";
import { variacaoValoresApi } from "../../../../../hooks/variacaoValoresApi.ts";
import { TipoVariacao, NivelVariacao, ValorVariacao } from "../../../../../types/Produto.ts";
import { VariationLevelContainer } from "./styles.ts";

const CRIAR_EDITAR_VALUE = "__criar_editar__";

interface VariationLevelInputProps {
  nivel: NivelVariacao | null;
  label: string;
  tiposDisponiveis: TipoVariacao[];
  todosOsTipos?: TipoVariacao[];
  onSelectTipo: (tipo: TipoVariacao) => void;
  onValoresChange: (valores: string[]) => void;
  onRemove: () => void;
  onOpenTiposModal: () => void;
  permitir_edicao: boolean;
  permitir_visual?: boolean;
  dragHandleProps?: any;
  helperText?: React.ReactNode;
  suggestions?: string[];
}

export const VariationLevelInput: React.FC<VariationLevelInputProps> = ({
  nivel,
  label,
  tiposDisponiveis,
  todosOsTipos,
  onSelectTipo,
  onValoresChange,
  onRemove,
  onOpenTiposModal,
  permitir_edicao,
  permitir_visual,
  dragHandleProps,
  helperText,
  suggestions = [],
}) => {
  const { theme } = useTheme();
  const [selectedValue, setSelectedValue] = useState("");
  const [changingType, setChangingType] = useState(false);
  // Swatches do tipo deste nível, indexados por chaveSwatch() (sem acento, minúsculo).
  const [swatches, setSwatches] = useState<Record<string, SwatchVariacao>>({});
  const [editandoValor, setEditandoValor] = useState<string | null>(null);

  const idTipo = nivel?.tipo.id;

  // Trocar o tipo do nível preserva os valores, mas o swatch mora em (tipo, valor) —
  // sem levar junto, a cor que o cliente já tinha posto sumiria da tela.
  const anteriorRef = useRef<{
    idTipo: number;
    swatches: Record<string, SwatchVariacao>;
  } | null>(null);
  const valoresRef = useRef<string[]>([]);
  valoresRef.current = nivel?.valores ?? [];

  // Só a troca manual pelo select migra. O drag-and-drop também troca o tipo deste
  // slot, mas lá os valores viajam junto com o tipo — o swatch continua achável no
  // dicionário e migrar copiaria (gravando) o swatch de um tipo para o outro.
  const trocaManualRef = useRef(false);

  const migrarSwatches = useCallback(
    async (
      anteriores: Record<string, SwatchVariacao>,
      atuais: Record<string, SwatchVariacao>,
      idTipoDestino: number
    ) => {
      const api = variacaoValoresApi();
      const migrados: Record<string, SwatchVariacao> = {};

      for (const valor of valoresRef.current) {
        const chave = chaveSwatch(valor);
        const origem = anteriores[chave];
        const destino = atuais[chave];
        const temOrigem = !!(origem?.cor_hex || origem?.imagem);
        const temDestino = !!(destino?.cor_hex || destino?.imagem);
        // Nunca sobrescreve o que o tipo de destino já tem cadastrado pra esse valor.
        if (!temOrigem || temDestino) continue;

        try {
          await api.salvarValor({
            id_tipo: idTipoDestino,
            valor,
            cor_hex: origem.cor_hex ?? "",
            imagem: origem.imagem ?? "",
          });
          migrados[chave] = {
            cor_hex: origem.cor_hex ?? null,
            imagem: origem.imagem ?? null,
          };
        } catch (error) {
          console.error("Erro ao migrar swatch da variação:", valor, error);
        }
      }

      return migrados;
    },
    []
  );

  const carregarSwatches = useCallback(async () => {
    // Consome já: uma troca que não chegue a carregar não pode migrar a próxima.
    const trocaManual = trocaManualRef.current;
    trocaManualRef.current = false;

    if (!idTipo) {
      anteriorRef.current = null;
      return;
    }
    try {
      const valores = await variacaoValoresApi().getValores(idTipo);
      const mapa: Record<string, SwatchVariacao> = {};
      valores.forEach((v: ValorVariacao) => {
        mapa[chaveSwatch(v.valor)] = { cor_hex: v.cor_hex, imagem: v.imagem };
      });

      const anterior = anteriorRef.current;
      if (trocaManual && anterior && anterior.idTipo !== idTipo) {
        Object.assign(mapa, await migrarSwatches(anterior.swatches, mapa, idTipo));
      }

      anteriorRef.current = { idTipo, swatches: mapa };
      setSwatches(mapa);
    } catch (error) {
      // Sem dicionário o chip volta a exibir o ícone padrão — não bloqueia a edição.
      console.error("Erro ao buscar valores de variação:", error);
    }
  }, [idTipo, migrarSwatches]);

  useEffect(() => {
    carregarSwatches();
  }, [carregarSwatches]);

  const handleAplicarSwatch = (novoNome: string, swatch: ValorVariacao | null) => {
    if (!editandoValor) return;

    setSwatches((atual) => {
      const atualizado = {
        ...atual,
        [chaveSwatch(novoNome)]: {
          cor_hex: swatch?.cor_hex ?? null,
          imagem: swatch?.imagem ?? null,
        },
      };
      // Mantém a referência em dia pra que uma cor recém-aplicada também migre na troca de tipo.
      if (idTipo) anteriorRef.current = { idTipo, swatches: atualizado };
      return atualizado;
    });

    // Renomear é local ao produto: troca a string na posição, sem tocar em outros produtos.
    if (novoNome !== editandoValor && nivel) {
      const index = nivel.valores.indexOf(editandoValor);
      if (index >= 0) {
        const atualizados = [...nivel.valores];
        atualizados[index] = novoNome;
        onValoresChange(atualizados);
      }
    }

    setEditandoValor(null);
  };

  if (!nivel) {
    // State 1: Selecting type
    const options = [
      ...tiposDisponiveis
        .filter((t) => t.ativo === 1)
        .map((t) => ({
          name: t.nome,
          value: t.id.toString(),
        })),
      { name: "Criar e editar variações", value: CRIAR_EDITAR_VALUE },
    ];

    return (
      <VariationLevelContainer theme={theme}>
        <div className="level-select-row">
          <Select
            label={label}
            options={options}
            value={selectedValue}
            placeholder="Selecione o tipo"
            onChange={(newValue) => {
              if (newValue === CRIAR_EDITAR_VALUE) {
                onOpenTiposModal();
                return;
              }
              setSelectedValue(newValue);
            }}
            disabled={!permitir_edicao}
          />
          <Button
            title="Adicionar"
            className="fonteTexto"
            width="20%"
            onClick={() => {
              if (!selectedValue) return;
              const tipo = tiposDisponiveis.find(
                (t) => t.id.toString() === selectedValue
              );
              if (tipo) {
                onSelectTipo(tipo);
                setSelectedValue("");
              }
            }}
            disabled={!selectedValue || !permitir_edicao}
          />
        </div>
      </VariationLevelContainer>
    );
  }

  // State 2: Inserting values
  const tiposParaTroca = todosOsTipos || tiposDisponiveis;
  const changeTypeOptions = [
    ...tiposParaTroca
      .filter((t) => t.ativo === 1 && t.id !== nivel.tipo.id)
      .map((t) => ({
        name: t.nome,
        value: t.id.toString(),
      })),
    { name: "Criar e editar variações", value: CRIAR_EDITAR_VALUE },
  ];

  return (
    <VariationLevelContainer theme={theme}>
      {changingType ? (
        <div className="level-change-type-row">
          <Select
            label={`Trocar tipo — ${label}`}
            options={changeTypeOptions}
            value=""
            placeholder={nivel.tipo.nome}
            onChange={(newValue) => {
              if (newValue === CRIAR_EDITAR_VALUE) {
                onOpenTiposModal();
                setChangingType(false);
                return;
              }
              const tipo = tiposParaTroca.find(
                (t) => t.id.toString() === newValue
              );
              if (tipo) {
                // Os valores ficam, o tipo muda: aqui sim o swatch precisa ir junto.
                trocaManualRef.current = true;
                onSelectTipo(tipo);
                setChangingType(false);
              }
            }}
            disabled={!permitir_edicao}
          />
          <span
            className="cancel-change fonteTexto"
            onClick={() => setChangingType(false)}
          >
            Cancelar
          </span>
        </div>
      ) : (
        <div className="level-tags-row">
          <TagValueInput
            label={
              <>
                {label} |{" "}
                <span
                  onClick={() => permitir_edicao && setChangingType(true)}
                  style={{ textDecoration: "underline", cursor: permitir_edicao ? "pointer" : "default" }}
                >
                  {nivel.tipo.nome}
                </span>
              </>
            }
            values={nivel.valores}
            onChange={onValoresChange}
            onClear={permitir_edicao ? onRemove : undefined}
            disabled={!permitir_edicao}
            dragHandleProps={dragHandleProps}
            helperText={helperText}
            suggestions={suggestions}
            swatches={swatches}
            onEditSwatch={(permitir_visual ?? permitir_edicao) ? setEditandoValor : undefined}
          />
        </div>
      )}

      <ModalEditarVariacao
        isOpen={!!editandoValor}
        setModal={(aberto) => !aberto && setEditandoValor(null)}
        idTipo={nivel.tipo.id}
        valor={editandoValor || ""}
        valorCadastrado={
          editandoValor
            ? ({
                ...swatches[chaveSwatch(editandoValor)],
                valor: editandoValor,
              } as any)
            : null
        }
        onAplicar={handleAplicarSwatch}
        permitirRenomear={permitir_edicao}
      />
    </VariationLevelContainer>
  );
};
